import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { GetTheApp } from "@/components/GetTheApp";
import InstagramFeed from "@/components/InstagramFeed";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight, Clock, MapPin, ShieldCheck, Star, Quote, CheckCircle2, Trophy, Instagram, Mic, Video as VideoIcon, MonitorPlay, ShoppingBag, ExternalLink } from "lucide-react";
import { useSEO } from "@/lib/useSEO";
import { useLandingLeadForms } from "@/pages/useLandingLeadForms";
import { api } from "@/lib/api";
import { DEFAULT_HOME } from "@/lib/homeDefaults";
import { locations } from "@/data/locations";
import { PHOTO_COACH_PORTRAIT as COACH_PHOTO, PHOTO_RING_PAD_WORK } from "@/lib/brand";

const ICONS = { Trophy, Clock, ShieldCheck, MapPin, MonitorPlay };

// Smart CTA link: #anchor → in-page, http(s) → external tab, else → SPA route.
function SmartLink({ href, className, children, testid }) {
  if (!href) return <span className={className}>{children}</span>;
  if (href.startsWith("#")) return <a href={href} className={className} data-testid={testid}>{children}</a>;
  if (/^https?:\/\//i.test(href)) return <a href={href} target="_blank" rel="noopener noreferrer" className={className} data-testid={testid}>{children}</a>;
  return <Link to={href} className={className} data-testid={testid}>{children}</Link>;
}

// "Zoom call" mockup featuring Coach Chris's real action photo — used as the virtual service image
function ZoomCallMockup() {
  return (
    <div className="absolute inset-0 bg-gradient-to-br from-[#1a1a1a] via-[#0a0a0a] to-[#000] p-2.5 sm:p-3 md:p-5 flex flex-col">
      <div className="flex items-center justify-between gap-2 text-zinc-400 text-[8px] sm:text-[9px] md:text-[10px] uppercase tracking-wider md:tracking-widest mb-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full bg-[#FF331F] animate-pulse flex-shrink-0" />
          <span className="truncate">Live · 39:17</span>
        </div>
        <div className="flex items-center gap-1.5 min-w-0">
          <MonitorPlay className="h-2.5 w-2.5 md:h-3 md:w-3 flex-shrink-0" />
          <span className="truncate"><span className="md:hidden">Flow State</span><span className="hidden md:inline">Flow State Method</span></span>
        </div>
      </div>
      <div className="relative flex-1 overflow-hidden bg-black border border-[#27272A]">
        <img src={PHOTO_RING_PAD_WORK} alt="Coach Chris coaching live" className="w-full h-full object-cover" />
        <div className="absolute bottom-1.5 left-1.5 sm:bottom-2 sm:left-2 bg-black/80 backdrop-blur-sm border border-zinc-700 px-1.5 py-0.5 sm:px-2 sm:py-1 text-[8px] sm:text-[9px] md:text-[10px] uppercase tracking-wider md:tracking-widest text-white flex items-center gap-1 sm:gap-1.5">
          <Mic className="h-2 w-2 sm:h-2.5 sm:w-2.5 text-emerald-400 flex-shrink-0" /> Coach Chris
        </div>
        <div className="absolute top-1.5 right-1.5 sm:top-2 sm:right-2 w-12 h-9 sm:w-16 sm:h-12 md:w-24 md:h-16 bg-[#0a0a0a] border border-[#27272A] grid place-items-center text-zinc-600">
          <VideoIcon className="h-3 w-3 md:h-4 md:w-4" />
        </div>
      </div>
      <div className="flex items-center justify-center gap-1.5 sm:gap-2 md:gap-3 mt-2 sm:mt-3">
        {[Mic, VideoIcon].map((I, i) => (
          <div key={i} className="h-7 w-7 sm:h-8 sm:w-8 grid place-items-center border border-[#27272A] bg-[#0F0F0F]">
            <I className="h-3 w-3 sm:h-3.5 sm:w-3.5 text-zinc-400" />
          </div>
        ))}
        <div className="h-7 sm:h-8 px-2.5 sm:px-3 grid place-items-center bg-[#FF331F] text-white text-[9px] sm:text-[10px] uppercase tracking-wider md:tracking-widest font-bold">End</div>
      </div>
    </div>
  );
}

export default function Landing() {
  const [cfg, setCfg] = useState(DEFAULT_HOME);
  useEffect(() => {
    api.get("/home-config").then(({ data }) => data && setCfg(data)).catch(() => {});
  }, []);

  useSEO({
    title: cfg.seo?.title || DEFAULT_HOME.seo.title,
    description: cfg.seo?.description || DEFAULT_HOME.seo.description,
    path: "/",
    ogImage: COACH_PHOTO,
  });
  const { form, setForm, submitting, submitLead, njForm, setNjForm, njSubmitting, submitInPerson } = useLandingLeadForms();

  const hero = cfg.hero || {};
  const svc = cfg.services || {};
  const featured = svc.featured || {};
  const single = svc.single || {};
  const about = cfg.about || {};
  const method = cfg.method || {};
  const inAction = cfg.in_action || {};
  const tst = cfg.testimonials || {};
  const apparel = cfg.apparel || {};
  const inPerson = cfg.in_person || {};
  const closer = cfg.closer || {};
  const contact = cfg.contact || {};
  const google = cfg.google || {};

  return (
    <div className="App relative">
      <SiteHeader />

      {/* HERO */}
      {hero.enabled !== false && (
      <section className="theme-on-dark relative min-h-[92vh] grain overflow-hidden" data-testid="hero-section">
        <img src={hero.bg_image} alt="Coach Chris training a fighter in the ring" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#050505] via-[#050505]/85 to-[#050505]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />

        <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-28 md:pt-32 pb-32">
          <div className="reveal max-w-4xl">
            <div className="inline-flex items-center gap-2 mb-6 text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold">
              <span className="h-px w-8 bg-[#FF331F]" /> {hero.kicker}
            </div>
            <h1 className="font-display font-bold uppercase text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.95] tracking-tight whitespace-pre-line">
              {hero.title_pre}<span className="text-[#FF331F]">{hero.title_accent}</span>{hero.title_post}
            </h1>
            <p className="mt-8 max-w-2xl text-zinc-300 text-base md:text-lg leading-relaxed whitespace-pre-line">{hero.subtitle}</p>

            {/* Two equal choice cards — Online vs In-Person */}
            <div className="mt-10 grid sm:grid-cols-2 gap-4 max-w-2xl">
              <SmartLink href={hero.online?.href} testid="hero-online-card"
                className="group flex items-start gap-4 border-2 border-[#FF331F] bg-[#FF331F]/10 hover:bg-[#FF331F]/20 p-5 rounded-sm transition-colors">
                <MonitorPlay className="h-7 w-7 text-[#FF331F] flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="font-display uppercase tracking-wide text-lg text-white flex items-center gap-2">
                    {hero.online?.label} <ArrowRight className="h-4 w-4 text-[#FF331F] group-hover:translate-x-1 transition-transform" />
                  </div>
                  <div className="text-xs text-zinc-300 mt-1">{hero.online?.sublabel}</div>
                </div>
              </SmartLink>
              <SmartLink href={hero.inperson?.href} testid="hero-inperson-card"
                className="group flex items-start gap-4 border-2 border-zinc-700 bg-zinc-900/40 hover:border-[#FF331F] hover:bg-zinc-900/70 p-5 rounded-sm transition-colors">
                <MapPin className="h-7 w-7 text-[#FF331F] flex-shrink-0 mt-0.5" />
                <div className="min-w-0">
                  <div className="font-display uppercase tracking-wide text-lg text-white flex items-center gap-2">
                    {hero.inperson?.label} <ArrowRight className="h-4 w-4 text-[#FF331F] group-hover:translate-x-1 transition-transform" />
                  </div>
                  <div className="text-xs text-zinc-300 mt-1">{hero.inperson?.sublabel}</div>
                </div>
              </SmartLink>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs uppercase tracking-widest text-zinc-400">
              {(hero.badges || []).map((b, i) => {
                const Icon = ICONS[b.icon] || Trophy;
                return (
                  <div key={i} className="flex items-center gap-2" data-testid={`hero-badge-${i}`}>
                    <Icon className="h-4 w-4 text-[#FF331F]" />{b.text}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 border-y border-[#27272A] bg-[#050505]/90 overflow-hidden">
          <div className="marquee flex whitespace-nowrap py-3 text-xs uppercase tracking-[0.4em] text-zinc-500">
            {[...Array(2)].flatMap((_, i) => [
              <span key={`a-${i}`} className="mx-8">Focus.</span>,
              <span key={`b-${i}`} className="mx-8 text-[#FF331F]">Discipline.</span>,
              <span key={`c-${i}`} className="mx-8">Flow State.</span>,
              <span key={`d-${i}`} className="mx-8">Anywhere in the world.</span>,
              <span key={`e-${i}`} className="mx-8 text-[#FF331F]">Train Smart.</span>,
              <span key={`f-${i}`} className="mx-8">Hit Harder.</span>,
            ])}
          </div>
        </div>
      </section>
      )}

      {/* SERVICES */}
      {svc.enabled !== false && (
      <section id="services" className="max-w-7xl mx-auto px-6 md:px-10 py-24" data-testid="services-section">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-3">{svc.kicker}</div>
            <h2 className="font-display font-bold uppercase text-4xl md:text-6xl tracking-tight leading-none">{svc.heading_line1}<br />{svc.heading_line2}</h2>
          </div>
          <p className="text-zinc-400 max-w-md text-sm md:text-base whitespace-pre-line">{svc.intro}</p>
        </div>

        {/* Featured Program */}
        <div className="border-2 border-[#FF331F] bg-[#0F0F0F] relative overflow-hidden mb-6" data-testid="service-card-featured">
          <div className="grid md:grid-cols-2">
            <div className="relative aspect-[4/3] md:aspect-auto overflow-hidden order-1 md:order-2 min-h-[380px]">
              <ZoomCallMockup />
              <div className="absolute top-4 right-4 bg-[#FF331F] text-white text-[10px] uppercase tracking-widest font-bold px-3 py-1.5 z-10">{featured.tag}</div>
            </div>
            <div className="p-8 md:p-12 order-2 md:order-1 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-3 text-xs text-zinc-500 uppercase tracking-widest mb-4">
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {featured.duration}</span>
                  <span className="h-1 w-1 bg-zinc-700 rounded-full" />
                  <span>{featured.location}</span>
                </div>
                <h3 className="font-display font-bold uppercase text-3xl md:text-5xl mb-4 leading-none">{featured.title}</h3>
                <p className="text-zinc-300 leading-relaxed mb-6 whitespace-pre-line">{featured.desc}</p>
                <ul className="space-y-2 mb-8">
                  {(featured.perks || []).map((p) => (
                    <li key={p.text}
                      className={p.highlight
                        ? "flex items-start gap-2 text-sm font-semibold text-[#FF331F] border border-[#FF331F]/40 bg-[#FF331F]/10 px-3 py-2 -mx-1"
                        : "flex items-start gap-2 text-sm text-zinc-400"}
                      data-testid={p.highlight ? "perk-highlight" : undefined}>
                      <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5 text-[#FF331F]" /> {p.text}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">{featured.price_note}</div>
                  <div className="font-display text-5xl text-white">${featured.price}</div>
                </div>
                <SmartLink href={featured.cta_href}
                  className="inline-flex items-center bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm px-4 py-2.5 text-sm" testid="featured-book-virtual">
                  {featured.cta_label} <ArrowRight className="ml-2 h-4 w-4" />
                </SmartLink>
              </div>
            </div>
          </div>
        </div>

        {/* Single Session card */}
        <div className="border border-[#27272A] bg-[#0F0F0F] p-6 md:p-8 grid md:grid-cols-12 gap-6 items-center" data-testid="service-card-single">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3 mb-3 flex-wrap">
              <span className="text-[10px] uppercase tracking-widest bg-zinc-900 border border-zinc-700 text-zinc-300 px-2 py-1">{single.tag}</span>
              <span className="flex items-center gap-1 text-xs text-zinc-500 uppercase tracking-widest"><Clock className="h-3 w-3" /> {single.duration}</span>
            </div>
            <h3 className="font-display font-bold uppercase text-2xl md:text-3xl mb-3">{single.title}</h3>
            <p className="text-zinc-400 text-sm leading-relaxed whitespace-pre-line">{single.desc}</p>
            <p className="text-[11px] text-zinc-500 mt-3 uppercase tracking-widest">Standard rate: ${single.price} · Alumni rate: ${single.alumni_price}</p>
          </div>
          <div className="md:col-span-5 flex flex-col items-stretch md:items-end gap-3">
            <div className="text-right hidden md:block">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Per session</div>
              <div className="font-display text-4xl text-white">${single.price}</div>
            </div>
            <SmartLink href={single.cta_href} testid="book-virtual-40-btn"
              className="inline-flex items-center justify-center border border-[#27272A] text-white hover:bg-[#FF331F] hover:border-[#FF331F] font-bold uppercase tracking-widest rounded-sm w-full bg-transparent px-4 py-2.5 text-sm transition-colors">
              Book single session — ${single.price} <ArrowRight className="ml-2 h-4 w-4" />
            </SmartLink>
          </div>
        </div>
      </section>
      )}

      {/* ABOUT */}
      {about.enabled !== false && (
      <section id="about" className="bg-[#0A0A0A] border-y border-[#27272A] py-24" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid md:grid-cols-2 gap-16 items-center">
          <div className="relative aspect-[4/5] overflow-hidden">
            <img src={about.image} alt="Coach Chris" className="w-full h-full object-cover" />
            <div className="absolute bottom-4 left-4 bg-[#050505] border border-[#27272A] px-4 py-3">
              <div className="text-[#FF331F] text-[10px] uppercase tracking-[0.3em] font-bold">Head Coach</div>
              <div className="font-display uppercase text-xl">Coach Chris</div>
            </div>
          </div>
          <div>
            <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-3">{about.kicker}</div>
            <h2 className="font-display font-bold uppercase text-4xl md:text-6xl tracking-tight leading-none">{about.heading_pre}<br />{about.heading_lead}<span className="text-[#FF331F]">{about.heading_accent}</span>{about.heading_post}</h2>
            <p className="text-zinc-300 mt-8 leading-relaxed whitespace-pre-line">{about.body1}</p>
            <p className="text-zinc-300 mt-4 leading-relaxed whitespace-pre-line">{about.body2}</p>

            <div className="mt-8 grid grid-cols-3 gap-4">
              {(about.stats || []).map((s, i) => (
                <div key={i} className="border border-[#27272A] bg-[#050505] p-4" data-testid={`stat-${i}`}>
                  <div className={`font-display text-3xl ${s.accent ? "text-[#FF331F]" : "text-white"}`}>{s.value}</div>
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">{s.label}</div>
                </div>
              ))}
            </div>

            <div className="mt-8">
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold mb-3">Specialties</div>
              <div className="flex flex-wrap gap-2">
                {(about.specialties || []).map((s) => (
                  <span key={s} className="text-[11px] uppercase tracking-widest border border-[#27272A] bg-[#050505] text-zinc-300 px-3 py-1.5">{s}</span>
                ))}
              </div>
            </div>

            <div className="mt-8 flex items-center gap-2 text-sm text-zinc-400">
              <MapPin className="h-4 w-4 text-[#FF331F]" /> {about.location_line}
            </div>
          </div>
        </div>
      </section>
      )}

      {/* BTB STATEMENT BANNER */}
      {method.enabled !== false && (
      <section className="theme-on-dark relative bg-[#050505] border-y border-[#27272A] overflow-hidden" data-testid="btb-statement-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-3">{method.kicker}</div>
            <h2 className="font-display font-bold uppercase text-3xl md:text-5xl tracking-tight leading-none mb-6">{method.heading}</h2>
            <p className="text-zinc-300 leading-relaxed text-sm md:text-base whitespace-pre-line">{method.body1}</p>
            <p className="text-zinc-400 leading-relaxed mt-4 text-sm whitespace-pre-line">{method.body2}</p>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2 relative">
            <img src={method.art_image} alt="Better Through Boxing" className="w-full h-auto max-h-[520px] object-contain select-none" draggable={false} data-testid="btb-grunge-art" />
          </div>
        </div>
      </section>
      )}

      {/* IN ACTION */}
      {inAction.enabled !== false && (
      <section className="bg-[#050505] py-24 relative overflow-hidden" data-testid="in-action-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
            <div>
              <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-3">{inAction.kicker}</div>
              <h2 className="font-display font-bold uppercase text-4xl md:text-6xl tracking-tight leading-none">{inAction.heading_line1}<br />{inAction.heading_line2}</h2>
            </div>
            <p className="text-zinc-400 max-w-md text-sm whitespace-pre-line">{inAction.subtitle}</p>
          </div>

          <div className="theme-on-dark grid grid-cols-12 grid-rows-2 gap-3 md:gap-4 h-[600px] md:h-[680px]">
            {(inAction.photos || []).slice(0, 3).map((ph, i) => {
              const layout = i === 0
                ? "col-span-12 md:col-span-7 row-span-2"
                : "col-span-6 md:col-span-5 row-span-1";
              return (
                <div key={i} className={`${layout} relative overflow-hidden border border-[#27272A] group`} data-testid={`action-photo-${i + 1}`}>
                  <img src={ph.image} alt={ph.caption} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/80 via-transparent to-transparent" />
                  {i === 0 ? (
                    <div className="absolute bottom-5 left-5 right-5">
                      {ph.kicker && <div className="text-[#FF331F] text-[10px] uppercase tracking-[0.3em] font-bold mb-1">{ph.kicker}</div>}
                      <div className="font-display uppercase text-2xl md:text-3xl tracking-tight">{ph.caption}</div>
                    </div>
                  ) : (
                    <div className="absolute bottom-3 left-4 text-[10px] uppercase tracking-widest text-white font-bold">{ph.caption}</div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-[#27272A] pt-6">
            <p className="text-zinc-400 text-sm">Want a closer look? <a href={`https://www.instagram.com/${inAction.instagram_handle}`} target="_blank" rel="noreferrer" className="text-[#FF331F] hover:underline">@{inAction.instagram_handle}</a> on Instagram.</p>
            <SmartLink href={inAction.cta_href} testid="action-book-cta"
              className="inline-flex items-center bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm px-4 py-2.5 text-sm">
              {inAction.cta_label} <ArrowRight className="ml-2 h-4 w-4" />
            </SmartLink>
          </div>
        </div>
      </section>
      )}

      {/* TESTIMONIALS */}
      {tst.enabled !== false && (
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-24" data-testid="testimonials-section">
        <div className="mb-12">
          <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-3">{tst.kicker}</div>
          <h2 className="font-display font-bold uppercase text-4xl md:text-6xl tracking-tight leading-none">{tst.heading_line1}<br />{tst.heading_line2}</h2>
        </div>

        <div className="relative border-2 border-[#FF331F] bg-gradient-to-br from-[#FF331F]/15 via-[#0F0F0F] to-[#0F0F0F] p-8 md:p-12 mb-6 overflow-hidden" data-testid="testimonial-featured">
          <div className="absolute top-0 right-0 bg-[#FF331F] text-white text-[10px] uppercase tracking-[0.3em] font-bold px-4 py-2">Virtual Client Spotlight</div>
          <div className="absolute -top-6 -right-6 text-[#FF331F]/10 pointer-events-none select-none"><Quote className="h-48 w-48" /></div>
          <div className="relative grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-2">
              <div className="h-20 w-20 md:h-24 md:w-24 grid place-items-center bg-[#FF331F] text-white font-display text-4xl md:text-5xl border-2 border-[#FF5242]" aria-hidden>
                <Quote className="h-10 w-10" />
              </div>
            </div>
            <div className="md:col-span-10">
              <div className="flex items-center gap-1 mb-4">
                {[1,2,3,4,5].map((n) => <Star key={n} className="h-4 w-4 fill-[#FF331F] text-[#FF331F]" />)}
                <span className="ml-3 text-[10px] uppercase tracking-[0.3em] text-zinc-400 font-bold">{tst.featured?.badge}</span>
              </div>
              <p className="text-white text-xl md:text-2xl font-display leading-snug mb-5">&ldquo;{tst.featured?.quote}&rdquo;</p>
              <div className="flex items-center gap-3">
                <div className="h-px w-8 bg-[#FF331F]" />
                <div className="text-xs uppercase tracking-[0.3em] text-[#FF331F] font-bold">{tst.featured?.author}</div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {(tst.items || []).map((t, i) => (
            <div key={i} className="border border-[#27272A] bg-[#0F0F0F] p-6 relative" data-testid={`testimonial-${i + 1}`}>
              <Quote className="h-6 w-6 text-[#FF331F] mb-3" />
              <div className="flex items-center gap-1 mb-3">
                {[1,2,3,4,5].map((n) => <Star key={n} className="h-3 w-3 fill-[#FF331F] text-[#FF331F]" />)}
              </div>
              <p className="text-zinc-200 leading-relaxed text-sm mb-5">&ldquo;{t.quote}&rdquo;</p>
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-bold">— {t.author}</div>
            </div>
          ))}
        </div>
      </section>
      )}

      {/* APPAREL TEASER */}
      {apparel.enabled !== false && (
      <section className="relative overflow-hidden bg-[#0A0A0A] border-y border-[#27272A]" data-testid="apparel-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="inline-flex items-center gap-2 mb-4">
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">{apparel.kicker}</span>
              {apparel.badge && <span className="text-[10px] uppercase tracking-widest bg-emerald-500/15 text-emerald-400 px-2 py-0.5 border border-emerald-500/40">{apparel.badge}</span>}
            </div>
            <h2 className="font-display font-bold uppercase text-3xl md:text-5xl tracking-tight leading-none mb-6">{apparel.heading}</h2>
            <p className="text-zinc-300 leading-relaxed mb-6 text-sm md:text-base whitespace-pre-line">{apparel.body}</p>
            <SmartLink href={apparel.cta_href} testid="apparel-shop-btn"
              className="inline-flex items-center gap-2 bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest text-sm rounded-sm px-7 py-3.5 transition-colors">
              <ShoppingBag className="h-4 w-4" /> {apparel.cta_label}
              <ExternalLink className="h-3.5 w-3.5 opacity-70" />
            </SmartLink>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2 relative h-80 md:h-[480px] flex items-center justify-center">
            <img src={apparel.image} alt="Better Through Boxing — Apparel" className="max-h-full w-auto object-contain drop-shadow-[0_20px_60px_rgba(255,51,31,0.35)] select-none" draggable={false} data-testid="apparel-tshirt" />
          </div>
        </div>
      </section>
      )}

      {/* IN-PERSON TRAINING — NJ ONLY CONTACT CAPTURE */}
      {inPerson.enabled !== false && (
      <section id="in-person" className="relative bg-[#050505] border-t border-[#27272A]" data-testid="inperson-section">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,51,31,0.08),transparent_50%)] pointer-events-none" />
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-24 grid lg:grid-cols-12 gap-12 relative">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 mb-4">
              <span className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">{inPerson.kicker}</span>
              {inPerson.badge && <span className="text-[10px] uppercase tracking-widest bg-[#FF331F] text-white px-2 py-0.5">{inPerson.badge}</span>}
            </div>
            <h2 className="font-display font-bold uppercase text-3xl md:text-5xl tracking-tight leading-none mb-6">
              {inPerson.heading_line1}<br />{inPerson.heading_line2}<br /><span className="text-[#FF331F]">{inPerson.heading_accent}</span>
            </h2>
            <p className="text-zinc-300 leading-relaxed mb-6 text-sm md:text-base whitespace-pre-line">{inPerson.body}</p>
            <ul className="space-y-2 text-sm text-zinc-400">
              {(inPerson.bullets || []).map((b, i) => (
                <li key={i} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-[#FF331F] flex-shrink-0 mt-0.5" /> {b}</li>
              ))}
            </ul>
          </div>

          <form onSubmit={submitInPerson} className="lg:col-span-7 bg-[#0F0F0F] border border-[#27272A] p-8 space-y-4" data-testid="inperson-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Full name *</label>
                <Input required value={njForm.name} onChange={(e) => setNjForm({ ...njForm, name: e.target.value })} placeholder="Jane Athlete" className="bg-[#050505] border-[#27272A] focus:border-[#FF331F] focus:ring-[#FF331F] rounded-sm h-12" data-testid="inperson-name" />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Email *</label>
                <Input required type="email" value={njForm.email} onChange={(e) => setNjForm({ ...njForm, email: e.target.value })} placeholder="you@example.com" className="bg-[#050505] border-[#27272A] focus:border-[#FF331F] focus:ring-[#FF331F] rounded-sm h-12" data-testid="inperson-email" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Phone *</label>
                <Input required type="tel" value={njForm.phone} onChange={(e) => setNjForm({ ...njForm, phone: e.target.value })} placeholder="+1 (555) 123-4567" className="bg-[#050505] border-[#27272A] focus:border-[#FF331F] focus:ring-[#FF331F] rounded-sm h-12" data-testid="inperson-phone" />
              </div>
              <div className="space-y-1.5">
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Zip code *</label>
                <Input required value={njForm.zip} onChange={(e) => setNjForm({ ...njForm, zip: e.target.value })} placeholder="07030" maxLength={10} className="bg-[#050505] border-[#27272A] focus:border-[#FF331F] focus:ring-[#FF331F] rounded-sm h-12 font-mono" data-testid="inperson-zip" />
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">Level of experience *</label>
              <select required value={njForm.experience} onChange={(e) => setNjForm({ ...njForm, experience: e.target.value })}
                className="w-full h-12 bg-[#050505] border border-[#27272A] focus:border-[#FF331F] focus:ring-[#FF331F] rounded-sm px-3 text-sm text-white uppercase tracking-widest appearance-none cursor-pointer" data-testid="inperson-experience">
                <option value="" disabled className="bg-[#0F0F0F]">Select your level…</option>
                <option value="Brand new — never trained" className="bg-[#0F0F0F]">Brand new — never trained</option>
                <option value="Beginner — under 1 year" className="bg-[#0F0F0F]">Beginner — under 1 year</option>
                <option value="Intermediate — 1-3 years" className="bg-[#0F0F0F]">Intermediate — 1-3 years</option>
                <option value="Advanced — 3+ years" className="bg-[#0F0F0F]">Advanced — 3+ years</option>
                <option value="Returning to boxing" className="bg-[#0F0F0F]">Returning to boxing</option>
                <option value="Active competitor" className="bg-[#0F0F0F]">Active competitor</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">What are you looking for?</label>
              <Textarea value={njForm.message} onChange={(e) => setNjForm({ ...njForm, message: e.target.value })} placeholder="Tell Coach Chris what you want to work on — technique, conditioning, sparring prep, weight loss, competition goals, weekly availability, etc." rows={5} className="bg-[#050505] border-[#27272A] focus:border-[#FF331F] focus:ring-[#FF331F] rounded-sm" data-testid="inperson-message" />
            </div>
            <Button type="submit" disabled={njSubmitting} className="w-full bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm py-6" data-testid="inperson-submit">
              {njSubmitting ? "Sending…" : "Request in-person training"} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <p className="text-[11px] text-zinc-500 text-center">Limited slots. NJ residents only. Coach Chris vets each request personally.</p>
          </form>
        </div>
      </section>
      )}

      {/* SERVICE AREAS — all GBP towns, fully listed, each links to its SEO landing page */}
      <section id="service-areas" className="bg-[#0A0A0A] border-t border-[#27272A] py-16 md:py-20" data-testid="service-areas-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-3 flex items-center gap-2">
            <MapPin className="h-4 w-4" /> Service Areas
          </div>
          <h2 className="font-display font-bold uppercase text-3xl md:text-5xl tracking-tight leading-none mb-4">In-person boxing across Central NJ</h2>
          <p className="text-zinc-400 text-sm md:text-base max-w-2xl mb-8">Coach Chris brings private, in-home training to your door throughout Somerset County and the surrounding Central Jersey towns below. Not local? Train live over Zoom from anywhere in the world.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5" data-testid="service-areas-grid">
            {locations.map((l) => (
              <Link key={l.slug} to={`/boxing-lessons/${l.slug}`} data-testid={`service-area-${l.slug}`}
                className="group flex items-center gap-2 border border-[#27272A] bg-[#0F0F0F] px-4 py-3 hover:border-[#FF331F] transition-colors">
                <MapPin className="h-3.5 w-3.5 text-[#FF331F] shrink-0" />
                <span className="text-sm text-zinc-200 group-hover:text-white truncate">{l.city}</span>
              </Link>
            ))}
          </div>
          <Link to="/boxing-lessons" className="inline-flex items-center gap-2 mt-6 text-[#FF331F] hover:underline text-sm uppercase tracking-widest font-bold" data-testid="service-areas-all">
            View all service areas <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* COACH CHRIS — FINAL CLOSER */}
      {closer.enabled !== false && (
      <section className="bg-[#0A0A0A] border-y border-[#27272A] py-20" data-testid="coach-closer-section">
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <div className="relative aspect-[3/4] border border-[#27272A] overflow-hidden bg-[#0F0F0F] max-w-sm mx-auto md:mx-0">
              <img src={closer.image} alt="Coach Chris — The Flow State Method" className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
              <div className="absolute top-4 left-4 bg-[#FF331F] text-white text-[10px] uppercase tracking-widest font-bold px-3 py-1.5">Coach</div>
              <div className="absolute bottom-5 left-5 right-5">
                <div className="font-display uppercase text-3xl tracking-tight">Coach Chris</div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-300 mt-1">The Flow State Method</div>
              </div>
            </div>
          </div>
          <div className="md:col-span-7">
            <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-3">{closer.kicker}</div>
            <h2 className="font-display font-bold uppercase text-4xl md:text-5xl tracking-tight leading-none mb-5">{closer.heading_line1}<br/>{closer.heading_line2}</h2>
            <p className="text-zinc-300 leading-relaxed mb-8 max-w-xl whitespace-pre-line">{closer.body}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <SmartLink href={closer.primary_href} testid="closer-book-program"
                className="inline-flex items-center justify-center bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm px-7 py-4 text-sm">
                {closer.primary_label} <ArrowRight className="ml-2 h-4 w-4" />
              </SmartLink>
              <SmartLink href={closer.secondary_href} testid="closer-book-single"
                className="inline-flex items-center justify-center border border-zinc-700 text-white hover:bg-zinc-900 font-bold uppercase tracking-widest rounded-sm px-7 py-4 bg-transparent text-sm transition-colors">
                {closer.secondary_label}
              </SmartLink>
            </div>
          </div>
        </div>
      </section>
      )}

      {/* CONTACT / MESSAGE COACH */}
      {contact.enabled !== false && (
      <section id="contact" className="max-w-5xl mx-auto px-6 md:px-10 py-24" data-testid="contact-section">
        <div className="grid md:grid-cols-5 gap-12">
          <div className="md:col-span-2">
            <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-3">{contact.kicker}</div>
            <h2 className="font-display font-bold uppercase text-3xl md:text-5xl tracking-tight leading-none">{contact.heading_line1}<br />{contact.heading_line2}</h2>
            <p className="text-zinc-400 mt-6 text-sm leading-relaxed whitespace-pre-line">
              {contact.body} <Link to="/book?package=virtual_40" className="text-[#FF331F] hover:underline">Book your first session →</Link>
            </p>
            <a href={`https://www.instagram.com/${contact.instagram_handle}`} target="_blank" rel="noreferrer" className="mt-8 inline-flex items-center gap-2 text-zinc-400 hover:text-white" data-testid="contact-instagram-link">
              <Instagram className="h-4 w-4 text-[#FF331F]" /> @{contact.instagram_handle}
            </a>
          </div>
          <form onSubmit={submitLead} className="md:col-span-3 bg-[#0F0F0F] border border-[#27272A] p-8 space-y-4" data-testid="lead-form">
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full name" className="bg-[#050505] border-[#27272A] focus:border-[#FF331F] focus:ring-[#FF331F] rounded-sm" data-testid="lead-name" />
            <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" placeholder="Email address" className="bg-[#050505] border-[#27272A] focus:border-[#FF331F] focus:ring-[#FF331F] rounded-sm" data-testid="lead-email" />
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone (optional)" className="bg-[#050505] border-[#27272A] focus:border-[#FF331F] focus:ring-[#FF331F] rounded-sm" data-testid="lead-phone" />
            <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="What are your goals? (technique, fitness, sparring, etc)" rows={4} className="bg-[#050505] border-[#27272A] focus:border-[#FF331F] focus:ring-[#FF331F] rounded-sm" data-testid="lead-message" />
            <Button type="submit" disabled={submitting} className="w-full bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest rounded-sm py-6" data-testid="lead-submit">
              {submitting ? "Sending…" : "Send Message"}
            </Button>
          </form>
        </div>
      </section>
      )}

      {/* GOOGLE BUSINESS — MAP + REVIEW (local SEO) */}
      {google.enabled !== false && (
      <section id="find-us" className="bg-[#0A0A0A] border-y border-[#27272A] py-16 md:py-20" data-testid="google-section">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-8 lg:gap-10 items-stretch">
          <div className="lg:col-span-5 flex flex-col justify-center">
            <div className="text-[#FF331F] text-xs uppercase tracking-[0.3em] font-bold mb-3 flex items-center gap-2">
              <MapPin className="h-4 w-4" /> {google.kicker}
            </div>
            <h2 className="font-display font-bold uppercase text-3xl md:text-4xl tracking-tight leading-none mb-4">{google.heading}</h2>
            <p className="text-zinc-300 leading-relaxed mb-7 text-sm md:text-base whitespace-pre-line">{google.subtext}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <a href={google.review_url} target="_blank" rel="noopener noreferrer" data-testid="leave-review-btn"
                className="inline-flex items-center justify-center gap-2 bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-widest text-sm rounded-sm px-6 py-3.5 transition-colors">
                <Star className="h-4 w-4 fill-white" /> Leave a Review
              </a>
              <a href={google.maps_url} target="_blank" rel="noopener noreferrer" data-testid="view-on-maps-btn"
                className="inline-flex items-center justify-center gap-2 border border-zinc-700 text-white hover:bg-zinc-900 hover:border-[#FF331F] font-bold uppercase tracking-widest text-sm rounded-sm px-6 py-3.5 bg-transparent transition-colors">
                <MapPin className="h-4 w-4 text-[#FF331F]" /> View on Google Maps
                <ExternalLink className="h-3.5 w-3.5 opacity-60" />
              </a>
            </div>
          </div>
          <div className="lg:col-span-7">
            <div className="relative w-full h-72 md:h-[420px] border border-[#27272A] overflow-hidden bg-black shadow-[0_20px_60px_rgba(0,0,0,0.5)]">
              {google.map_embed_url ? (
                <iframe title="The Flow State Method LLC on Google Maps" src={google.map_embed_url}
                  className="w-full h-full grayscale-[0.2] contrast-110" style={{ border: 0 }}
                  loading="lazy" referrerPolicy="no-referrer-when-downgrade" allowFullScreen data-testid="google-map" />
              ) : (
                <div className="w-full h-full grid place-items-center text-zinc-600 text-sm">Map unavailable</div>
              )}
            </div>
          </div>
        </div>
      </section>
      )}

      {cfg.instagram_feed?.enabled !== false && (
      <section className="max-w-7xl mx-auto px-6 md:px-10">
        <InstagramFeed placement="landing" limit={6} title={cfg.instagram_feed?.title || "Latest from the gym."} />
      </section>
      )}

      {cfg.get_the_app?.enabled !== false && (
      <section className="max-w-7xl mx-auto px-6 md:px-10 mt-16 mb-20">
        <GetTheApp />
      </section>
      )}

      <SiteFooter />
    </div>
  );
}
